import { ChevronDown } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { packChatPreview } from "@/payroll/pack-chat/message-preview";
import {
  PACK_CHAT_MARK_MODE,
  PackChatStatusMark,
} from "@/payroll/pack-chat/pack-chat-status-mark";
import { referenceParts } from "@/payroll/pack-chat/reference-label";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";

export const PackChatThreadHeader = ({
  thread,
  expanded,
  onToggle,
}: {
  thread: IPackChatThreadSummary;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { path, label, value, valueIsFigure } = referenceParts(thread.context);
  const open = thread.status === "open";
  const count = thread.message_count;
  const last = thread.last_message;
  const preview = last ? packChatPreview(last) : null;
  const figure = value && valueIsFigure ? value : undefined;
  const messageCount = count
    ? `${count} message${count === 1 ? "" : "s"}`
    : null;
  const when = dayjs(thread.updated_at).fromNow();
  const meta = [path, messageCount, when].filter(Boolean).join(" · ");
  const phoneMeta = [figure, path, when].filter(Boolean).join(" · ");

  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className={cn("w-full px-4 py-3 text-left transition-colors", {
        "hover:bg-surface-secondary": !expanded,
      })}
    >
      <Stack direction="row" align="start" justify="between" gap="md" fullWidth>
        <Stack gap="none" align="stretch" className="min-w-0">
          <Typography
            variant="subtitleW3"
            className={cn("truncate", {
              "text-secondary-text": !open,
            })}
          >
            {label}
          </Typography>
          <Typography
            variant="bodyW3"
            textColor="tertiary"
            className="truncate text-[13px]"
          >
            <span className="sm:hidden">{phoneMeta}</span>
            <span className="hidden sm:inline">{meta}</span>
          </Typography>
          {!expanded && last && (
            <Typography
              variant="bodyW3"
              textColor="tertiary"
              className="truncate pt-0.5 text-[13px]"
            >
              {preview &&
                (preview.author
                  ? `${preview.author}: ${preview.body}`
                  : preview.body)}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          align="center"
          gap="sm"
          className="shrink-0 pt-0.5"
        >
          {figure && (
            <Typography variant="subtitleW3" className="hidden sm:block">
              {figure}
            </Typography>
          )}
          <PackChatStatusMark
            focusable={false}
            hasAiMessages={thread.has_ai_messages}
            needsManager={thread.needs_manager}
            responseOwner={thread.response_owner}
            mode={PACK_CHAT_MARK_MODE.owner}
            open={open}
            label={open ? "Open" : "Closed"}
          />
          <ChevronDown
            className={cn("size-4 text-tertiary-text transition-transform", {
              "rotate-180": expanded,
            })}
            aria-hidden
          />
        </Stack>
      </Stack>
    </button>
  );
};
